import { useEffect, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../../ui/command";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { PrcDialogContent } from "./PrcDialogContent";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { cn } from "../../utils/common/cn";
import {
  DIMENSION_META,
  dimensionMeta,
  useScenarioFieldOptions,
  useScenarioInvestorCodes,
  type ScenarioClause,
  type ScenarioCondition,
  type ScenarioField,
  type ScenarioFieldOptions,
  type ScenarioOperator,
} from "../../utils/portfolio-risk-concentration/api/scenario";

/** Fields whose value picks from real, observed data (useScenarioFieldOptions) rather than a
 *  fixed enum or free text. investorCode is handled separately -- it reuses the governed
 *  destination-code list already loaded for this same dialog. */
const _DB_BACKED_FIELDS: Partial<Record<ScenarioField, keyof ScenarioFieldOptions>> = {
  riskRating: "riskRating",
  termMonths: "termMonths",
  state: "state",
  assetType: "assetType",
  paymentType: "paymentType",
  sicCode: "sicCode",
  vendorNumber: "vendorNumber",
};

/** sicCode/vendorNumber run into the thousands of real values -- a plain dropdown would
 *  render every option at once. These two get a searchable combobox instead; everything else
 *  in _DB_BACKED_FIELDS is small enough (single/low-double digits) for a plain Select. */
const _SEARCHABLE_FIELDS = new Set<ScenarioField>(["sicCode", "vendorNumber"]);

/** A searchable single-select over a real, potentially long value list (SIC Code, Vendor) --
 *  Popover + cmdk, the standard shadcn combobox shape. Values are always rendered as strings;
 *  the caller's own valueType coercion (draftToClause) is unaffected. */
function FieldValueCombobox({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: (string | number)[];
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-9 flex-1 justify-between text-xs font-normal"
        >
          <span className="truncate">{value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput placeholder={`Search ${placeholder.toLowerCase()}…`} className="h-9 text-xs" />
          <CommandList>
            <CommandEmpty>No match.</CommandEmpty>
            <CommandGroup>
              {options.map((o) => {
                const v = String(o);
                return (
                  <CommandItem
                    key={v}
                    value={v}
                    onSelect={() => {
                      onChange(v);
                      setOpen(false);
                    }}
                    className="text-xs"
                  >
                    <Check className={cn("mr-2 h-3.5 w-3.5", value === v ? "opacity-100" : "opacity-0")} />
                    {v}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

const _OPERATOR_LABEL: Record<ScenarioOperator, string> = {
  EQ: "=",
  NE: "≠",
  LT: "<",
  LTE: "≤",
  GT: ">",
  GTE: "≥",
};

interface ClauseDraft {
  field: ScenarioField;
  operator: ScenarioOperator;
  value: string;
}

// Effective Date removed from THIS dialog's own field picker only, per direct instruction --
// DIMENSION_META itself stays whole so dimensionMeta()'s lookup keeps resolving the label for
// any condition saved earlier with field: "dateBooked" (its own summary/badge elsewhere would
// otherwise silently fall back to DIMENSION_META[0]'s label instead of "Effective Date").
const SELECTABLE_DIMENSIONS = DIMENSION_META.filter((d) => d.field !== "dateBooked");

function emptyClause(field?: ScenarioField): ClauseDraft {
  const meta = dimensionMeta(field ?? DIMENSION_META[0].field);
  return { field: meta.field, operator: meta.operators[0], value: "" };
}

function clauseToDraft(c: ScenarioClause): ClauseDraft {
  return { field: c.field, operator: c.operator, value: String(c.value) };
}

function draftToClause(d: ClauseDraft): ScenarioClause {
  const meta = dimensionMeta(d.field);
  let value: string | number | boolean = d.value;
  if (meta.valueType === "boolean") value = d.value === "true";
  else if (meta.valueType === "number") value = Number(d.value);
  return { field: d.field, operator: d.operator, value };
}

/**
 * Add a new scenario step, or adjust an existing one: the "Add scenario step" popup. Every
 * clause picks from the fixed, backend-known dimension list (DIMENSION_META) via a picker,
 * never free text: there is deliberately no way to type an arbitrary new dimension name,
 * even though early mockups for this feature showed a text field for that. "+ Add new
 * dimension" below is the same fixed list shown a second way, for adding a field that isn't
 * already part of one of this step's conditions.
 *
 * Destination is a real investor code from useScenarioInvestorCodes() (the same governed
 * athena_input.investor_code_listing the Scenario Portfolio page's source picker already
 * reads) plus a facility, or Exclude, never a pre-paired list and never free-text entry.
 * Deliberately NOT limited to codes already pledged into a facility
 * (prc_facility_investor_code_map): proposing a pledge for a code that ISN'T pledged into
 * anything yet is the whole point of this feature.
 */
export function ScenarioStepDialog({
  open,
  onOpenChange,
  editCondition,
  sourceInvestorCode,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editCondition: ScenarioCondition | null;
  /** Excluded from the destination code list: pledging code 10 back into code 10 isn't a
   *  real option. */
  sourceInvestorCode: string;
  onSubmit: (condition: ScenarioCondition) => void;
}) {
  const codesQ = useScenarioInvestorCodes();
  const fieldOptionsQ = useScenarioFieldOptions();
  // ScenarioApplyStep seeds its row list with a placeholder condition ({ clauses: [],
  // destination: null }) before the viewer ever touches it -- opening THAT one for "Adjust"
  // passes a non-null editCondition, but nothing in it was ever actually configured. Treated
  // as a real edit, it would restore destination: null (Exclude) and defeat the new-condition
  // defaults below the moment the very first step is opened. "Pristine" (no clauses, no
  // destination) is the signal that distinguishes a never-configured placeholder from a
  // genuinely saved Exclude condition (which DOES have clauses, even with destination null).
  const isPristine = (c: ScenarioCondition) => c.clauses.length === 0 && !c.destination;
  const isEdit = !!editCondition && !isPristine(editCondition);
  const destinationCodes = (codesQ.data ?? []).filter((c) => c.code !== sourceInvestorCode);

  const [clauses, setClauses] = useState<ClauseDraft[]>([emptyClause()]);
  const [exclude, setExclude] = useState(false);
  const [destCode, setDestCode] = useState("");
  const [destFacility, setDestFacility] = useState<string>("CAP1");
  const [error, setError] = useState<string | null>(null);

  /** The facility a destination code belongs to, per Manage Capital: one book, never chosen here. Empty when the code is not mapped to a
   *  book. (Should a code ever be mapped to both, the Facility dropdown offers just those two.) */
  const facilityOptions: string[] = (codesQ.data ?? []).find((c) => c.code === destCode)?.facilities ?? [];
  // The facility follows the code: whenever the code (or the loaded list) changes, it becomes that code's own facility, or nothing.
  useEffect(() => {
    if (!open || exclude) return;
    setDestFacility((current) => (current && facilityOptions.includes(current) ? current : (facilityOptions[0] ?? "")));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, exclude, destCode, codesQ.data]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    // Clauses and destination are restored INDEPENDENTLY of each other. A condition starts with NO filters: a new one added here
    // (editCondition === null) has none, and one being edited shows exactly the clauses it has (possibly none). The viewer adds the
    // filters they want.
    setClauses(editCondition ? editCondition.clauses.map(clauseToDraft) : []);
    if (editCondition) {
      // Restore whatever destination this condition already carries, including the
      // auto-assigned default -- resetting it just because clauses were still empty would
      // silently discard a real (if auto-picked) destination the viewer may already be
      // relying on.
      setExclude(!editCondition.destination);
      setDestCode(editCondition.destination?.investorCode ?? "");
      setDestFacility(editCondition.destination?.facilityCode ?? "");
    } else {
      // Exclude off, Facility defaulted to the first option (CAP1) -- Investor Code defaults
      // to the first entry too, once destinationCodes loads (see the effect below; it isn't
      // known synchronously here, since useScenarioInvestorCodes is an async query).
      setExclude(false);
      setDestCode("");
      setDestFacility("CAP1");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editCondition]);

  // Seeds Investor Code to the first available destination once the list loads -- only for a
  // NEW condition (isEdit already restored the saved destination above) and only while it's
  // still unset, so this never clobbers a choice the viewer already made.
  useEffect(() => {
    if (!open || isEdit || destCode || destinationCodes.length === 0) return;
    // the first code that belongs to a facility, so the Facility shows by itself; the first code overall when none does
    setDestCode((destinationCodes.find((c) => c.facilities.length > 0) ?? destinationCodes[0]).code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, destCode, destinationCodes]);

  function updateClause(i: number, patch: Partial<ClauseDraft>) {
    setClauses((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  function setClauseField(i: number, field: ScenarioField) {
    updateClause(i, emptyClause(field));
  }

  function addClause() {
    setClauses((rows) => [...rows, emptyClause()]);
  }

  function removeClause(i: number) {
    setClauses((rows) => rows.filter((_, idx) => idx !== i));
  }

  function submit() {
    for (const c of clauses) {
      if (!c.value.trim()) {
        setError("Every condition needs a value. Remove the row or fill it in.");
        return;
      }
    }
    if (!exclude && (!destCode || !destFacility)) {
      setError(
        destCode
          ? `Investor code ${destCode} is not mapped to a facility in Manage Capital, so it cannot be a destination. Pick another code or choose Exclude.`
          : "Pick a destination code, or choose Exclude.",
      );
      return;
    }
    const destination = exclude ? null : { investorCode: destCode, facilityCode: destFacility };
    onSubmit({ clauses: clauses.map(draftToClause), destination });
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PrcDialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit scenario step" : "Add scenario step"}</DialogTitle>
          <DialogDescription>
            Combine any number of conditions, picked from the fields already in the contract
            data.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {error}
            </div>
          )}

          <div className="space-y-2">
            {clauses.map((c, i) => {
              const meta = dimensionMeta(c.field);
              return (
                <div key={i} className="flex items-center gap-2">
                  <Select value={c.field} onValueChange={(v) => setClauseField(i, v as ScenarioField)}>
                    <SelectTrigger className="h-9 w-48 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SELECTABLE_DIMENSIONS.map((d) => (
                        <SelectItem key={d.field} value={d.field}>
                          {d.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={c.operator}
                    onValueChange={(v) => updateClause(i, { operator: v as ScenarioOperator })}
                  >
                    <SelectTrigger className="h-9 w-20 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {meta.operators.map((op) => (
                        <SelectItem key={op} value={op}>
                          {_OPERATOR_LABEL[op]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {meta.valueType === "boolean" ? (
                    <Select value={c.value} onValueChange={(v) => updateClause(i, { value: v })}>
                      <SelectTrigger className="h-9 flex-1 text-xs">
                        <SelectValue placeholder="Yes / No" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">Yes</SelectItem>
                        <SelectItem value="false">No</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : meta.valueType === "enum" ? (
                    <Select value={c.value} onValueChange={(v) => updateClause(i, { value: v })}>
                      <SelectTrigger className="h-9 flex-1 text-xs">
                        <SelectValue placeholder="Value" />
                      </SelectTrigger>
                      <SelectContent>
                        {(meta.enumValues ?? []).map((v) => (
                          <SelectItem key={v} value={v}>
                            {v}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : c.field === "investorCode" ? (
                    // The same governed list the Destination picker below already loaded --
                    // one query, two pickers, never free text for a code.
                    <Select value={c.value} onValueChange={(v) => updateClause(i, { value: v })}>
                      <SelectTrigger className="h-9 flex-1 text-xs">
                        <SelectValue placeholder="Investor Code" />
                      </SelectTrigger>
                      <SelectContent>
                        {(codesQ.data ?? []).map((code) => (
                          <SelectItem key={code.code} value={code.code}>
                            {code.codeMapping ? `${code.code}: ${code.codeMapping}` : code.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : _DB_BACKED_FIELDS[c.field] && _SEARCHABLE_FIELDS.has(c.field) ? (
                    <FieldValueCombobox
                      value={c.value}
                      onChange={(v) => updateClause(i, { value: v })}
                      options={fieldOptionsQ.data?.[_DB_BACKED_FIELDS[c.field]!] ?? []}
                      placeholder={meta.label}
                    />
                  ) : _DB_BACKED_FIELDS[c.field] ? (
                    <Select value={c.value} onValueChange={(v) => updateClause(i, { value: v })}>
                      <SelectTrigger className="h-9 flex-1 text-xs">
                        <SelectValue placeholder="Value" />
                      </SelectTrigger>
                      <SelectContent>
                        {(fieldOptionsQ.data?.[_DB_BACKED_FIELDS[c.field]!] ?? []).map((v) => (
                          <SelectItem key={v} value={String(v)}>
                            {v}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      className="h-9 flex-1 text-xs [&::-webkit-calendar-picker-indicator]:invert"
                      type={meta.valueType === "number" ? "number" : meta.valueType === "date" ? "date" : "text"}
                      placeholder="value"
                      value={c.value}
                      onChange={(e) => updateClause(i, { value: e.target.value })}
                    />
                  )}

                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 w-9 shrink-0 p-0 text-muted-foreground"
                    onClick={() => removeClause(i)}
                  >
                    ×
                  </Button>
                </div>
              );
            })}
          </div>

          <Button variant="ghost" size="sm" onClick={addClause}>
            + Add condition
          </Button>

          <div>
            <Label>Destination</Label>
            <div className="mt-1 flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-xs">
                <Checkbox checked={exclude} onCheckedChange={(v) => setExclude(!!v)} />
                Exclude
              </label>
              {!exclude && (
                <>
                  <Select value={destCode} onValueChange={setDestCode}>
                    <SelectTrigger className="h-9 w-40 text-xs">
                      <SelectValue placeholder="Investor Code" />
                    </SelectTrigger>
                    <SelectContent>
                      {destinationCodes.map((c) => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.codeMapping ? `${c.code}: ${c.codeMapping}` : c.code}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* Read-only: a code belongs to one facility (Manage Capital decides which). Blank when it has none. */}
                  <Select
                    value={destFacility}
                    onValueChange={setDestFacility}
                    disabled={facilityOptions.length <= 1}
                  >
                    <SelectTrigger className="h-9 w-40 text-xs" aria-label="Facility">
                      <SelectValue placeholder="" />
                    </SelectTrigger>
                    <SelectContent>
                      {facilityOptions.map((f) => (
                        <SelectItem key={f} value={f}>
                          {f}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              )}
            </div>
            {!exclude && destCode && facilityOptions.length === 0 && (
              <p className="mt-1 text-[11px] text-amber">
                Code {destCode} is not mapped to a facility in Manage Capital, so it cannot be a destination yet. Pick another code, or map this
                one there.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit}>{isEdit ? "Save" : "Add to scenario"}</Button>
        </DialogFooter>
      </PrcDialogContent>
    </Dialog>
  );
}
